/* =========================================================
   El registro de auditoría, visible.

   El portal apunta cada cambio de campo desde el primer día —de qué
   valor a cuál, quién, cuándo y desde qué IP— y hasta ahora no había
   forma de leerlo sin entrar por SSH y escribir SQL. Guardaba una
   trazabilidad que nadie podía usar.

   La lista se pagina desde el servidor: la tabla crece sin límite y
   traerla entera al navegador dejaría de funcionar el día que haga
   falta de verdad. «Cargar más» pide la página siguiente por clave, no
   por posición, así que un cambio que ocurra mientras se lee no repite
   ni salta filas.
   ========================================================= */
import { useCallback, useEffect, useState } from 'react';
import { ic, sized } from '@/components/Icons';
import { BLUE_HEADER, DataTable, Section, type Column } from '@/components/ui';
import { api } from '@/lib/api';
import { useI18n } from '@/i18n';
import { useAppStore } from '@/store/useAppStore';

/** La firma abierta es lo que pide `DataTable`; los campos de verdad son
 *  los de abajo. */
interface Entrada extends Record<string, unknown> {
  id: string;
  entity: string;
  entityId: string;
  subject: string;
  field: string;
  from: string | null;
  to: string | null;
  action: string;
  actorId: string | null;
  actor: string;
  actorEmail: string;
  ip: string;
  at: string;
}

interface Actor { id: string; name: string; email: string; count: number }

const POR_PAGINA = 50;

const ENTIDADES = [
  'organization', 'site', 'instructor', 'assignment', 'app_user',
  'compliance_doc', 'disassociation_request', 'legal_contact_request', 'update_window',
] as const;

const NOMBRE_ENTIDAD: Record<string, { es: string; en: string }> = {
  organization: { es: 'Organización', en: 'Organization' },
  site: { es: 'Site', en: 'Site' },
  instructor: { es: 'Instructor', en: 'Instructor' },
  assignment: { es: 'Asignación', en: 'Assignment' },
  app_user: { es: 'Cuenta', en: 'Account' },
  compliance_doc: { es: 'Documento', en: 'Document' },
  disassociation_request: { es: 'Desasociación', en: 'Disassociation' },
  legal_contact_request: { es: 'Contacto legal', en: 'Legal contact' },
  update_window: { es: 'Ventana', en: 'Window' },
};

const NOMBRE_ACCION: Record<string, { es: string; en: string; tono: string }> = {
  create: { es: 'Alta', en: 'Created', tono: 'var(--ok)' },
  update: { es: 'Cambio', en: 'Changed', tono: 'var(--brand-primary)' },
  delete: { es: 'Baja', en: 'Deleted', tono: 'var(--err)' },
  import: { es: 'Importación', en: 'Import', tono: 'var(--warn)' },
};

/** aaaa-mm-dd de hace N días, para los atajos de fecha. */
function haceDias(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

/** Un valor largo cortado, pero sin esconder que se cortó. */
function corto(v: string | null, max = 60): string {
  if (v == null || v === '') return '—';
  return v.length > max ? v.slice(0, max) + '…' : v;
}

interface Filtros {
  entity: string;
  entityId: string;
  action: string;
  actor: string;
  desde: string;
  hasta: string;
  q: string;
}

const VACIO: Filtros = { entity: '', entityId: '', action: '', actor: '', desde: '', hasta: '', q: '' };

function comoQuery(f: Filtros, extra: Record<string, string> = {}): string {
  const p = new URLSearchParams();
  Object.entries({ ...f, ...extra }).forEach(([k, v]) => { if (v) p.set(k, v); });
  return p.toString();
}

export function AuditLog() {
  const { lang } = useI18n();
  const addToast = useAppStore((s) => s.addToast);

  const [filtros, setFiltros] = useState<Filtros>(VACIO);
  /** Los filtros de la consulta en curso; cambian solo al pulsar Buscar,
   *  para que escribir en una caja no dispare una consulta por tecla. */
  const [aplicados, setAplicados] = useState<Filtros>(VACIO);
  const [entradas, setEntradas] = useState<Entrada[]>([]);
  const [siguiente, setSiguiente] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [cargando, setCargando] = useState(false);
  const [fallo, setFallo] = useState('');
  const [actores, setActores] = useState<Actor[]>([]);
  const [detalle, setDetalle] = useState<Entrada | null>(null);

  const cargar = useCallback(async (f: Filtros, before: string | null) => {
    setCargando(true);
    setFallo('');
    try {
      const qs = comoQuery(f, {
        limit: String(POR_PAGINA),
        ...(before ? { before } : {}),
      });
      const r = await api.get<{ entries: Entrada[]; nextBefore: string | null }>(`/api/audit?${qs}`);
      setEntradas((prev) => (before ? [...prev, ...r.entries] : r.entries));
      setSiguiente(r.nextBefore);
      if (!before) {
        // el total va aparte: contar es lo caro y la lista no tiene por
        // qué esperarlo para dibujarse
        void api.get<{ count: number }>(`/api/audit/count?${comoQuery(f)}`)
          .then((c) => setTotal(c.count))
          .catch(() => setTotal(null));
      }
    } catch (err) {
      setFallo(err instanceof Error ? err.message : String(err));
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => {
    void cargar(VACIO, null);
    void api.get<{ actors: Actor[] }>('/api/audit/actors')
      .then((r) => setActores(r.actors))
      .catch(() => { /* el desplegable se queda vacío; se puede filtrar igual */ });
  }, [cargar]);

  function buscar() {
    setAplicados(filtros);
    setTotal(null);
    void cargar(filtros, null);
  }

  function limpiar() {
    setFiltros(VACIO);
    setAplicados(VACIO);
    setTotal(null);
    void cargar(VACIO, null);
  }

  async function exportar() {
    try {
      const blob = await api.bytes(`/api/audit.csv?${comoQuery(aplicados)}`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `auditoria-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      addToast({
        kind: 'ok',
        title: lang === 'es' ? 'Exportación lista' : 'Export ready',
        body: lang === 'es' ? 'Con los filtros aplicados' : 'With the applied filters',
      });
    } catch (err) {
      addToast({
        kind: 'err',
        title: lang === 'es' ? 'No se pudo exportar' : 'Export failed',
        body: err instanceof Error ? err.message : String(err),
      });
    }
  }

  const etiqueta = (m: Record<string, { es: string; en: string }>, k: string) =>
    m[k] ? m[k][lang] : k;

  const columnas: Column<Entrada>[] = [
    {
      key: 'at',
      label: lang === 'es' ? 'Cuándo' : 'When',
      render: (r) => {
        const d = new Date(r.at);
        return (
          <div style={{ whiteSpace: 'nowrap' }}>
            <div style={{ fontSize: 12 }}>{d.toLocaleDateString(lang === 'es' ? 'es-MX' : 'en-US')}</div>
            <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
              {d.toLocaleTimeString(lang === 'es' ? 'es-MX' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        );
      },
    },
    {
      key: 'action',
      label: lang === 'es' ? 'Acción' : 'Action',
      render: (r) => {
        const a = NOMBRE_ACCION[r.action];
        return (
          <span
            className="chip"
            style={{ borderColor: a?.tono, color: a?.tono, fontSize: 11, whiteSpace: 'nowrap' }}
          >
            {a ? a[lang] : r.action}
          </span>
        );
      },
    },
    {
      key: 'subject',
      label: lang === 'es' ? 'Sobre qué' : 'Subject',
      render: (r) => (
        <div style={{ minWidth: 160 }}>
          <div style={{ fontSize: 12, fontWeight: 600 }}>
            {r.subject || <span style={{ color: 'var(--text-3)', fontWeight: 400 }}>{r.entityId}</span>}
          </div>
          <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
            {etiqueta(NOMBRE_ENTIDAD, r.entity)}
            {r.subject ? ` · ${r.entityId}` : ''}
          </div>
        </div>
      ),
    },
    {
      key: 'field',
      label: lang === 'es' ? 'Qué cambió' : 'What changed',
      render: (r) => (
        r.field
          ? <span className="mono" style={{ fontSize: 11.5 }}>{r.field}</span>
          : <span style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
              {lang === 'es' ? '(el registro entero)' : '(the whole record)'}
            </span>
      ),
    },
    {
      key: 'valores',
      label: lang === 'es' ? 'De → a' : 'From → to',
      render: (r) => (
        // Sin campo, `to` lleva el detalle del alta o la baja, no un valor
        r.field ? (
          <div style={{ fontSize: 11.5, maxWidth: 320 }}>
            <span style={{ color: 'var(--text-3)', textDecoration: 'line-through' }}>{corto(r.from, 34)}</span>
            <span style={{ margin: '0 6px', color: 'var(--text-3)' }}>→</span>
            <span style={{ fontWeight: 600 }}>{corto(r.to, 34)}</span>
          </div>
        ) : (
          <div style={{ fontSize: 11.5, maxWidth: 320, color: 'var(--text-2)' }}>{corto(r.to, 70)}</div>
        )
      ),
    },
    {
      key: 'actor',
      label: lang === 'es' ? 'Quién' : 'Who',
      render: (r) => (
        <div style={{ minWidth: 130 }}>
          <div style={{ fontSize: 12 }}>
            {r.actor || <span style={{ color: 'var(--text-3)' }}>
              {lang === 'es' ? 'el sistema' : 'the system'}
            </span>}
          </div>
          {r.ip ? (
            <div className="mono" style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{r.ip}</div>
          ) : null}
        </div>
      ),
    },
  ];

  const hayFiltro = JSON.stringify(aplicados) !== JSON.stringify(VACIO);

  const caja = {
    padding: '7px 9px', borderRadius: 6, fontSize: 12.5,
    border: '1px solid var(--line-2)', background: 'var(--bg-1)', color: 'inherit',
  } as const;

  const rotulo = (texto: string) => (
    <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase' }}>
      {texto}
    </span>
  );

  return (
    <Section
      title={lang === 'es' ? 'Registro de auditoría' : 'Audit log'}
      tone="var(--brand-primary)"
      defaultOpen={false}
      right={
        <span className="chip" style={{ fontSize: 11 }}>
          {total == null
            ? (lang === 'es' ? 'todo el historial' : 'full history')
            : `${total.toLocaleString(lang === 'es' ? 'es-MX' : 'en-US')} ${lang === 'es' ? 'entradas' : 'entries'}`}
        </span>
      }
    >
      <p style={{ marginTop: 4, fontSize: 12.5, color: 'var(--text-2)' }}>
        {lang === 'es'
          ? 'Cada cambio de cada campo, con quién lo hizo, cuándo y desde qué dirección. No se puede editar ni borrar desde el portal: es el registro al que ETC tiene que poder recurrir cuando un socio discute un dato.'
          : 'Every change to every field, with who made it, when, and from which address. It cannot be edited or deleted from the portal: this is the record ETC falls back on when a partner disputes a value.'}
      </p>

      {/* ---- filtros ---- */}
      <div
        className="m-grid1"
        style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 10, marginTop: 12, alignItems: 'end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {rotulo(lang === 'es' ? 'Buscar' : 'Search')}
          <input
            style={caja}
            value={filtros.q}
            placeholder={lang === 'es' ? 'nombre, valor, campo…' : 'name, value, field…'}
            onChange={(e) => setFiltros({ ...filtros, q: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') buscar(); }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {rotulo(lang === 'es' ? 'Sobre qué' : 'Entity')}
          <select style={caja} value={filtros.entity} onChange={(e) => setFiltros({ ...filtros, entity: e.target.value })}>
            <option value="">{lang === 'es' ? 'Todo' : 'All'}</option>
            {ENTIDADES.map((e) => (
              <option key={e} value={e}>{etiqueta(NOMBRE_ENTIDAD, e)}</option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {rotulo(lang === 'es' ? 'Acción' : 'Action')}
          <select style={caja} value={filtros.action} onChange={(e) => setFiltros({ ...filtros, action: e.target.value })}>
            <option value="">{lang === 'es' ? 'Todas' : 'All'}</option>
            {Object.keys(NOMBRE_ACCION).map((a) => (
              <option key={a} value={a}>{NOMBRE_ACCION[a][lang]}</option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {rotulo(lang === 'es' ? 'Quién' : 'Who')}
          <select style={caja} value={filtros.actor} onChange={(e) => setFiltros({ ...filtros, actor: e.target.value })}>
            <option value="">{lang === 'es' ? 'Cualquiera' : 'Anyone'}</option>
            {actores.map((a) => (
              <option key={a.id} value={a.id}>{a.name} ({a.count})</option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {rotulo(lang === 'es' ? 'Desde' : 'From')}
          <input type="date" style={caja} value={filtros.desde} onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })} />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {rotulo(lang === 'es' ? 'Hasta' : 'To')}
          <input type="date" style={caja} value={filtros.hasta} onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })} />
        </label>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
        <button className="btn primary sm" onClick={buscar} disabled={cargando}>
          {sized(ic.search, 14)} {lang === 'es' ? 'Buscar' : 'Search'}
        </button>
        <button className="btn sm" onClick={limpiar} disabled={cargando || !hayFiltro}>
          {lang === 'es' ? 'Quitar filtros' : 'Clear filters'}
        </button>
        <span style={{ flex: 1 }} />
        {[7, 30, 90].map((d) => (
          <button
            key={d}
            className="btn sm ghost"
            style={{ fontSize: 11 }}
            onClick={() => {
              const f = { ...filtros, desde: haceDias(d), hasta: '' };
              setFiltros(f);
              setAplicados(f);
              setTotal(null);
              void cargar(f, null);
            }}
          >
            {lang === 'es' ? `Últimos ${d} días` : `Last ${d} days`}
          </button>
        ))}
        <button className="btn sm" onClick={() => void exportar()} disabled={cargando || entradas.length === 0}>
          {sized(ic.download, 14)} CSV
        </button>
      </div>

      {fallo ? (
        <div
          role="alert"
          style={{
            display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 12, padding: '10px 12px',
            border: '1px solid var(--err)', borderRadius: 8, fontSize: 12.5, color: 'var(--err)',
          }}
        >
          <span style={{ flexShrink: 0, display: 'inline-flex' }}>{ic.warn}</span>
          <span>{fallo}</span>
        </div>
      ) : null}

      <div style={{ marginTop: 12 }}>
        <DataTable
          columns={columnas}
          rows={entradas}
          onRowClick={(r) => setDetalle(r)}
          empty={
            cargando
              ? (lang === 'es' ? 'Cargando…' : 'Loading…')
              : hayFiltro
                ? (lang === 'es' ? 'Ningún cambio coincide con ese filtro.' : 'No changes match that filter.')
                : (lang === 'es' ? 'Todavía no hay ningún cambio registrado.' : 'No changes recorded yet.')
          }
        />
      </div>

      {siguiente ? (
        <div style={{ textAlign: 'center', marginTop: 10 }}>
          <button className="btn sm" disabled={cargando} onClick={() => void cargar(aplicados, siguiente)}>
            {cargando
              ? (lang === 'es' ? 'Cargando…' : 'Loading…')
              : (lang === 'es' ? `Cargar ${POR_PAGINA} más` : `Load ${POR_PAGINA} more`)}
          </button>
        </div>
      ) : entradas.length > 0 ? (
        <div style={{ textAlign: 'center', marginTop: 10, fontSize: 11.5, color: 'var(--text-3)' }}>
          {lang === 'es' ? 'No hay más entradas.' : 'No more entries.'}
        </div>
      ) : null}

      {/* Una entrada entera: los valores completos, sin cortar. */}
      {detalle ? (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setDetalle(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 60,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div
            className="card"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 640, width: '100%', maxHeight: '80vh', overflowY: 'auto' }}
          >
            <div style={{ ...BLUE_HEADER, padding: '12px 16px', fontWeight: 700, fontSize: 13 }}>
              {etiqueta(NOMBRE_ENTIDAD, detalle.entity)} · {NOMBRE_ACCION[detalle.action]?.[lang] ?? detalle.action}
            </div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12.5 }}>
              {([
                [lang === 'es' ? 'Sobre' : 'Subject', detalle.subject || '—'],
                [lang === 'es' ? 'Identificador' : 'Identifier', detalle.entityId],
                [lang === 'es' ? 'Campo' : 'Field', detalle.field || (lang === 'es' ? '(el registro entero)' : '(the whole record)')],
                [lang === 'es' ? 'Valor anterior' : 'Previous value', detalle.from ?? '—'],
                [lang === 'es' ? 'Valor nuevo' : 'New value', detalle.to ?? '—'],
                [lang === 'es' ? 'Quién' : 'Who', detalle.actor || (lang === 'es' ? 'el sistema' : 'the system')],
                [lang === 'es' ? 'Correo' : 'Email', detalle.actorEmail || '—'],
                ['IP', detalle.ip || '—'],
                [lang === 'es' ? 'Cuándo' : 'When', new Date(detalle.at).toLocaleString(lang === 'es' ? 'es-MX' : 'en-US')],
              ] as const).map(([k, v]) => (
                <div key={k} style={{ display: 'grid', gridTemplateColumns: '150px 1fr', gap: 10 }}>
                  <span style={{ color: 'var(--text-3)', fontSize: 11.5 }}>{k}</span>
                  <span style={{ wordBreak: 'break-word' }}>{v}</span>
                </div>
              ))}
            </div>
            <div style={{ padding: '0 16px 16px', textAlign: 'right' }}>
              <button className="btn" onClick={() => setDetalle(null)}>
                {lang === 'es' ? 'Cerrar' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </Section>
  );
}
